import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import { MyAccountMenuItemType } from '@utils/types'
import { ChevronDown } from '@components/icons'
import styles from './MyAccountMenuItem.module.sass'


interface Props {
  item?: MyAccountMenuItemType
  selectedItem?: any
  placeholder?: any
  initOpen?: boolean
  isActive?: boolean
  clickedSubItem?: any
  setClickedSubItem?: (v: any) => void
  orientation?: 'vertical' | 'horizontal'
  onClick?: (e) => void
  className?: string
}

const MyAccountMenuItem = (props: Props) => {

  const { 
    item = undefined,
    selectedItem = undefined,
    initOpen = false,
    isActive = false,
    clickedSubItem = undefined,
    setClickedSubItem = undefined,
    orientation = 'vertical',
    onClick = undefined,
    className = '',
  } = props

  const t = useTrans()
  const ref = useRef(null)
  const [open, setOpen] = useState(initOpen)
  const [horizontalOpen, setHorizontalOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!!ref.current && !ref.current?.contains(e.target)){
        setMobileOpen(false)
        setHorizontalOpen(false)
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape'){
        setMobileOpen(false)
        setHorizontalOpen(false)
      }
    }
    
    const handleFocus = (e) => {
      if (!!ref.current && !ref.current?.contains(e.target)){
        setMobileOpen(false)
        setHorizontalOpen(false)
      }
    }

    window.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('focusin', handleFocus)
    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('focusin', handleFocus)
    }
  }, [ref.current, orientation])

  useEffect(() => {
    selectedItem === item?.key && setOpen(true)
  }, [selectedItem])

  const getMenuItemContent = () => (
    <>
      {!!item.icon && <span className={styles.icon}>{item.icon}</span>}
      <span className={styles.label}>
        {t(item.placeholder)}
      </span>
      
    </>
  )

  return item ? (
    <li className={cn(styles.root, 
      isActive ? styles['root--active'] : null, 
      open ? styles['root--open'] : null, 
      mobileOpen ? styles['root--mobileOpen'] : null, 
      horizontalOpen ? styles['root--horizontalOpen'] : null, 
      styles[`root--${orientation}`],
      className)}
      ref={ref}
    >
      {!!item.subItems?.length ? (
        <>
          <button 
            className={styles.trigger} 
            type="button"
            aria-expanded={open}
            aria-haspopup={true}
            onClick={(e) => {
              setOpen(prev => !prev)
              setMobileOpen(prev => !prev)
              setHorizontalOpen(prev => !prev)
              if (onClick)
                onClick(e)
            }}
          >
            {getMenuItemContent()}
            <ChevronDown className={styles.arrow} />
          </button> 
          <ul role="menu" className={styles.dropdown}>
            {item.subItems.map((x) => (
              <li role="none" key={x.key}>
                <Link
                  href={x.route || ''}
                  role="menuitem"
                  onClick={() => {
                    setClickedSubItem(x.key)
                    setOpen(true)
                  }}
                  className={cn(styles.subitem, clickedSubItem === x?.key ? styles['subitem--active'] : '')}
                >
                  {t(x.placeholder)}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        !item.route && !!onClick ? (
          <button type="button" role="menuitem" onClick={onClick} className={styles.trigger}>
            {getMenuItemContent()} 
          </button>
        ) : (
          <Link href={item.route || ''} role="menuitem" onClick={onClick} target={item.newPage ? '_blank' : undefined} className={styles.link}>
            {getMenuItemContent()}
          </Link>
        )
      )}
    </li>
  ) : <></>
}

export default MyAccountMenuItem
